/**
 * API Client — 基于原生 fetch 的 HTTP 请求封装
 * 所有 API 模块应通过此文件导入，禁止直接使用 fetch/axios
 *
 * 特性：
 * - 自动注入 x-token Header（通过 setTokenProvider 回调模式避免循环依赖）
 * - 自动注入 X-Request-ID Header（若请求未携带则生成 UUID，后端中间件复用）
 * - 统一错误处理：code=7 Token 过期 → IPC 刷新 → 自动重试
 * - 指数退避重试（仅网络错误和 5xx）
 * - AbortController 超时控制（默认 30s）
 * - 请求取消支持（createAbortGroup）
 */
import { ipcCall } from '@/composables/useIPC'
import { PSI_ACCESS_DISABLED_CODE, PSI_ACCESS_DISABLED_EVENT } from '@/constants/psiAccess'
import { isDesktopRuntime } from '@/platform/bridge'
import { IpcCommand } from '@/types/ipc'
import { env } from '@/utils/env'

// ─── 常量 ───────────────────────────────────────────────
const BASE_URL = env.API_BASE_URL
const DEFAULT_TIMEOUT = 30_000
const MAX_RETRIES = 3
const RETRY_DELAYS = [1000, 2000, 4000]

function shouldSendParamValue(value: unknown) {
  return value !== undefined && value !== null && value !== ''
}

// ─── X-Request-ID 生成 ──────────────────────────────────
/**
 * 生成 X-Request-ID
 * 优先 crypto.randomUUID（现代浏览器/WebView 均支持），回退时间戳+随机数
 */
export function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

// ─── Token Provider & Updater ───────────────────────────
let tokenProvider: () => string = () => ''
let tokenUpdater: (token: string) => void = () => { }
let psiAccessDisabledHandler: (() => void) | null = null

export function setTokenProvider(provider: () => string) {
  tokenProvider = provider
}

/** 获取当前 Token（供导出等非标准请求场景直接使用） */
export function getToken(): string {
  return tokenProvider()
}

/** 注册 Token 更新回调（刷新成功后同步写回 store，避免竞态） */
export function setTokenUpdater(updater: (token: string) => void) {
  tokenUpdater = updater
}

export function setPsiAccessDisabledHandler(handler: (() => void) | null) {
  psiAccessDisabledHandler = handler
}

// ─── 自定义错误类 ───────────────────────────────────────
export class TokenExpiredError extends Error {
  constructor() {
    super('登录已过期，请重新登录')
    this.name = 'TokenExpiredError'
  }
}

interface BusinessErrorOptions {
  status?: number
  reasonCode?: string
  retryable?: boolean
  mappedStatus?: number
  upstreamStatus?: number
  providerRequestId?: string
  providerErrorCode?: string
  providerErrorType?: string
  providerErrorParam?: string
}

export class BusinessError extends Error {
  code: number
  status?: number
  reasonCode?: string
  retryable?: boolean
  mappedStatus?: number
  upstreamStatus?: number
  providerRequestId?: string
  providerErrorCode?: string
  providerErrorType?: string
  providerErrorParam?: string
  constructor(code: number, msg: string, options: BusinessErrorOptions = {}) {
    super(msg)
    this.name = 'BusinessError'
    this.code = code
    this.status = options.status
    this.reasonCode = options.reasonCode
    this.retryable = options.retryable
    this.mappedStatus = options.mappedStatus
    this.upstreamStatus = options.upstreamStatus
    this.providerRequestId = options.providerRequestId
    this.providerErrorCode = options.providerErrorCode
    this.providerErrorType = options.providerErrorType
    this.providerErrorParam = options.providerErrorParam
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

// ─── 指数退避重试（仅网络错误和 5xx） ───────────────────
async function fetchWithRetry(
  url: string,
  init: RequestInit,
  retries = 0,
  maxRetries = MAX_RETRIES,
): Promise<Response> {
  try {
    const res = await fetch(url, init)
    if (res.status >= 500 && retries < maxRetries) {
      await new Promise((r) => setTimeout(r, RETRY_DELAYS[retries]))
      return fetchWithRetry(url, init, retries + 1, maxRetries)
    }
    return res
  } catch (error) {
    if (init.signal?.aborted || (error as Error)?.name === 'AbortError') throw error
    if (retries < maxRetries) {
      await new Promise((r) => setTimeout(r, RETRY_DELAYS[retries]))
      return fetchWithRetry(url, init, retries + 1, maxRetries)
    }
    throw new NetworkError((error as Error).message)
  }
}

async function parseApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const text = await response.text()
  try {
    return JSON.parse(text) as ApiResponse<T>
  } catch {
    if (!response.ok) {
      throw new NetworkError(`请求失败：${response.status}`)
    }
    throw new NetworkError('服务响应格式异常，请稍后重试')
  }
}

// ─── Token 刷新并发控制（Promise 锁） ────────────────────
let refreshPromise: Promise<string> | null = null

async function refreshToken(): Promise<string> {
  if (refreshPromise) return refreshPromise
  refreshPromise = ipcCall<{ success: boolean; token?: string }>(IpcCommand.AuthHandleTokenExpired)
    .then((r) => {
      if (r?.success && r.token) {
        // 同步更新 store，确保重试请求拿到新 token
        tokenUpdater(r.token)
        return r.token
      }
      const err = new TokenExpiredError()
      handleTokenExpired()
      throw err
    })
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

// ─── Tauri 环境检测 ─────────────────────────────────────
const isTauri = isDesktopRuntime

// ─── 公司停用/删除全局拦截（code=9） ──────────────────────
function handleCompanyDeleted(code: number, msg: string) {
  if (code === 9 && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('company-deleted', { detail: msg }))
  }
}

// ─── 账号被禁用全局拦截（code=9003） ─────────────────────
function handleUserDisabled(code: number, msg: string) {
  if (code === 9003 && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('force-logout', { detail: msg || '账号已被管理员禁用' }))
  }
}

function handlePsiAccessDisabled(code: number) {
  if (code !== PSI_ACCESS_DISABLED_CODE || typeof window === 'undefined') return
  if (psiAccessDisabledHandler) {
    psiAccessDisabledHandler()
    return
  }
  window.dispatchEvent(new CustomEvent(PSI_ACCESS_DISABLED_EVENT))
}

function handleBusinessFailure(code: number, msg: string) {
  handleCompanyDeleted(code, msg)
  handleUserDisabled(code, msg)
  handlePsiAccessDisabled(code)
}

function safeBusinessReasonCode(value: unknown) {
  const code = String(value || '')
    .trim()
    .toLowerCase()
  return /^[a-z][a-z0-9_]{1,63}$/.test(code) ? code : undefined
}

function safeBusinessDiagnosticText(value: unknown, maxLength = 128) {
  const text = String(value || '').trim()
  if (!text) return undefined
  return text.length > maxLength ? `${text.slice(0, maxLength)}...[truncated]` : text
}

function safeBusinessStatus(value: unknown) {
  const status = Number(value || 0)
  return Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined
}

function businessErrorMessage(json: ApiResponse<unknown>, fallback = '请求失败') {
  const dataRecord =
    json.data && typeof json.data === 'object' ? (json.data as Record<string, unknown>) : undefined
  const reasonCode = safeBusinessReasonCode(dataRecord?.reasonCode)
  const detail = typeof dataRecord?.detail === 'string' ? dataRecord.detail.trim() : ''
  if (reasonCode === 'studio_unavailable' && detail) return detail
  return json.msg || fallback
}

function buildBusinessErrorOptions(
  json: ApiResponse<unknown>,
  fallbackStatus?: number,
): BusinessErrorOptions {
  const responseRecord = json as unknown as Record<string, unknown>
  const dataRecord =
    json.data && typeof json.data === 'object' ? (json.data as Record<string, unknown>) : undefined
  const statusValue = Number(responseRecord._httpStatus || fallbackStatus || 0)
  return {
    status: Number.isFinite(statusValue) && statusValue > 0 ? statusValue : undefined,
    reasonCode:
      safeBusinessReasonCode(dataRecord?.reasonCode) ||
      safeBusinessReasonCode(dataRecord?.errorCode) ||
      safeBusinessReasonCode(responseRecord.reasonCode) ||
      safeBusinessReasonCode(responseRecord.errorCode),
    retryable:
      typeof dataRecord?.retryable === 'boolean'
        ? dataRecord.retryable
        : typeof responseRecord.retryable === 'boolean'
          ? responseRecord.retryable
          : undefined,
    mappedStatus:
      safeBusinessStatus(dataRecord?.mappedStatus) ||
      safeBusinessStatus(responseRecord.mappedStatus),
    upstreamStatus:
      safeBusinessStatus(dataRecord?.upstreamStatus) ||
      safeBusinessStatus(responseRecord.upstreamStatus),
    providerRequestId:
      safeBusinessDiagnosticText(dataRecord?.providerRequestId) ||
      safeBusinessDiagnosticText(responseRecord.providerRequestId),
    providerErrorCode:
      safeBusinessDiagnosticText(dataRecord?.providerErrorCode) ||
      safeBusinessDiagnosticText(responseRecord.providerErrorCode),
    providerErrorType:
      safeBusinessDiagnosticText(dataRecord?.providerErrorType) ||
      safeBusinessDiagnosticText(responseRecord.providerErrorType),
    providerErrorParam:
      safeBusinessDiagnosticText(dataRecord?.providerErrorParam) ||
      safeBusinessDiagnosticText(responseRecord.providerErrorParam),
  }
}

// ─── Token 过期全局通知 ────────────────────────────────────
let _tokenExpiredDispatched = false
function handleTokenExpired() {
  if (_tokenExpiredDispatched) return // 防止并发请求重复弹窗
  _tokenExpiredDispatched = true
  window.dispatchEvent(new CustomEvent('token-expired'))
  // 5 秒后重置标记，允许用户在登录页重新登录后再次触发
  setTimeout(() => {
    _tokenExpiredDispatched = false
  }, 5000)
}

// ─── IPC 代理请求（Tauri 桌面端） ───────────────────────
async function requestViaIPC<T>(url: string, options?: RequestOptions): Promise<T> {
  // 将 params 转为 Record<string, string>（Rust 端期望的类型）
  let params: Record<string, string> | undefined
  if (options?.params && typeof options.params === 'object') {
    params = {}
    for (const [k, v] of Object.entries(options.params)) {
      if (shouldSendParamValue(v)) {
        params[k] = String(v)
      }
    }
  }

  try {
    // 构造 X-Request-ID（若请求已携带则复用，否则自动生成）
    const existingHeaders = options?.headers as Record<string, string> | undefined
    const requestId = existingHeaders?.['X-Request-ID'] || generateRequestId()

    // 合并已有 headers（保留调用方自定义 header，X-Request-ID 不覆盖已有值）
    const mergedHeaders: Record<string, string> = {
      ...(existingHeaders && typeof existingHeaders === 'object' ? existingHeaders : {}),
      'X-Request-ID': requestId,
    }

    const json = await ipcCall<ApiResponse<T>>(
      IpcCommand.ApiProxy,
      {
        method: options?.method || 'GET',
        path: url,
        body: options?.body ?? null,
        params: params ?? null,
        headers: mergedHeaders,
        timeoutMs: options?.timeout ?? undefined,
      },
      { timeout: options?.timeout ?? DEFAULT_TIMEOUT, signal: options?.signal ?? undefined },
    )

    // HTTP 401（Rust 端注入 _httpUnauthorized 标记）：Token 过期，尝试刷新重试
    // 注意：业务 code=7（如 Casbin "权限不足"）不会带此标记，不触发刷新
    if ((json as unknown as Record<string, unknown>)._httpUnauthorized) {
      // 账号被禁用(9003) / 公司被停用(9)：属于强制登出场景，禁止走 refresh 重试
      // 否则 refresh 失败会抛 TokenExpiredError，业务层 catch 后只显示错误提示，
      // 不会派发 force-logout / company-deleted 全局事件，导致"只报错不退出"。
      if (json.code === 9003 || json.code === 9) {
        handleBusinessFailure(json.code, json.msg)
        throw new BusinessError(
          json.code,
          businessErrorMessage(json),
          buildBusinessErrorOptions(json),
        )
      }
      if (!tokenProvider()) {
        throw new TokenExpiredError()
      }
      await refreshToken()
      const retryJson = await ipcCall<ApiResponse<T>>(
        IpcCommand.ApiProxy,
        {
          method: options?.method || 'GET',
          path: url,
          body: options?.body ?? null,
          params: params ?? null,
          headers: mergedHeaders,
          timeoutMs: options?.timeout ?? undefined,
        },
        { timeout: options?.timeout ?? DEFAULT_TIMEOUT, signal: options?.signal ?? undefined },
      )
      if (retryJson.code !== 0) {
        handleBusinessFailure(retryJson.code, retryJson.msg)
        throw new BusinessError(
          retryJson.code,
          businessErrorMessage(retryJson),
          buildBusinessErrorOptions(retryJson),
        )
      }
      return retryJson.data
    }

    if (json.code !== 0) {
      handleBusinessFailure(json.code, json.msg)
      throw new BusinessError(
        json.code,
        businessErrorMessage(json),
        buildBusinessErrorOptions(json),
      )
    }

    return json.data
  } catch (error) {
    if (error instanceof TokenExpiredError) throw error
    if (error instanceof BusinessError) throw error
    if ((error as Error)?.name === 'AbortError') throw error
    // IPC 层错误转为 NetworkError
    throw new NetworkError((error as Error).message || '网络异常')
  }
}

// ─── fetch 请求（非 Tauri 浏览器开发环境） ──────────────
async function requestViaFetch<T>(url: string, options?: RequestOptions): Promise<T> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), options?.timeout ?? DEFAULT_TIMEOUT)

  // 合并 signal：支持外部传入的 abort signal
  const externalSignal = options?.signal as AbortSignal | undefined
  const abortFromExternal = () => {
    controller.abort(externalSignal?.reason)
  }
  if (externalSignal) {
    if (externalSignal.aborted) {
      abortFromExternal()
    } else {
      externalSignal.addEventListener('abort', abortFromExternal, { once: true })
    }
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    }

    // 统一注入 X-Request-ID（若请求未携带则自动生成）
    if (!headers['X-Request-ID']) {
      headers['X-Request-ID'] = generateRequestId()
    }

    const token = tokenProvider()
    if (token) {
      headers['x-token'] = token
    }

    const { timeout: _t, retry: _retry, params, body, ...restOptions } = options || {}

    // 拼接 query params 到 URL
    let fullUrl = BASE_URL + url
    if (params && typeof params === 'object') {
      const query = new URLSearchParams()
      for (const [k, v] of Object.entries(params)) {
        if (shouldSendParamValue(v)) {
          query.append(k, String(v))
        }
      }
      const qs = query.toString()
      if (qs) {
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs
      }
    }

    const res = await fetchWithRetry(
      fullUrl,
      {
        ...restOptions,
        signal: controller.signal,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      0,
      options?.retry === false ? 0 : MAX_RETRIES,
    )

    // Token 过期处理：HTTP 401 → 通过 Promise 锁并发安全刷新 → 成功重试一次 → 失败抛错
    if (res.status === 401) {
      if (options?._isRetry) {
        handleTokenExpired()
        throw new TokenExpiredError()
      }
      await refreshToken()
      return request<T>(url, { ...options, _isRetry: true })
    }

    const json = await parseApiResponse<T>(res)

    if (json.code !== 0) {
      handleBusinessFailure(json.code, json.msg)
      throw new BusinessError(
        json.code,
        businessErrorMessage(json),
        buildBusinessErrorOptions(json, res.status),
      )
    }

    return json.data
  } catch (error) {
    if (error instanceof TokenExpiredError) throw error
    if (error instanceof BusinessError) throw error
    if (error instanceof NetworkError) throw error
    if ((error as Error).name === 'AbortError') {
      throw new NetworkError('请求超时或已取消')
    }
    throw new NetworkError((error as Error).message || '网络异常')
  } finally {
    externalSignal?.removeEventListener('abort', abortFromExternal)
    clearTimeout(timeout)
  }
}

// ─── 核心请求方法 ────────────────────────────────────────
export async function request<T>(url: string, options?: RequestOptions): Promise<T> {
  // Tauri 桌面端：通过 Rust IPC 代理（Token 自动注入，无 CORS）
  if (isTauri) {
    return requestViaIPC<T>(url, options)
  }

  // 非 Tauri 环境（浏览器开发）：走原有 fetch 逻辑
  return requestViaFetch<T>(url, options)
}

interface ApiDownloadResponse {
  contentBase64: string
  contentType: string
  filename?: string | null
  filePath?: string | null
  downloadDir?: string | null
  businessCode?: number | null
  businessMessage?: string | null
}

interface UploadOptions extends RequestOptions {
  params?: Record<string, unknown>
  /** Multipart-only fields, never copied to the URL/query log. */
  formFields?: Record<string, string>
  /** Supported only by explicitly enabled multipart endpoints in the Rust proxy. */
  additionalFiles?: File[]
}

interface DownloadOptions extends RequestOptions {
  filename?: string
}

export interface DownloadResult {
  filename: string
  locationText: string
  filePath?: string
  downloadDir?: string
}

function getDefaultDownloadLocationText() {
  return isTauri ? '系统默认下载目录' : '浏览器默认下载目录'
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const commaIdx = result.indexOf(',')
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result)
    }
    reader.onerror = () => reject(new Error('读取文件失败'))
    reader.readAsDataURL(blob)
  })
}

function fileToBase64(file: File): Promise<string> {
  return blobToBase64(file)
}

function paramsToRecord(params?: Record<string, unknown>) {
  if (!params || typeof params !== 'object') return null
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (shouldSendParamValue(value)) {
      result[key] = String(value)
    }
  }
  return result
}

function saveBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  try {
    a.click()
  } finally {
    a.remove()
    // Keep the Blob available while the browser/WebView consumes the download navigation.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  }
}

function normalizeApiBlobTarget(url: string) {
  const baseUrl = new URL(BASE_URL)
  const targetUrl = new URL(url, baseUrl)
  if (targetUrl.origin !== baseUrl.origin) {
    throw new NetworkError('二进制响应地址不受信任')
  }
  const ipcPathname = targetUrl.pathname.startsWith('/api/')
    ? targetUrl.pathname.slice('/api'.length)
    : targetUrl.pathname
  return {
    targetUrl,
    path: `${ipcPathname}${targetUrl.search}`,
  }
}

async function fetchApiBlobViaIPC(url: string, options?: RequestOptions): Promise<Blob> {
  const { path } = normalizeApiBlobTarget(url)
  const existingHeaders = options?.headers as Record<string, string> | undefined
  const requestId = existingHeaders?.['X-Request-ID'] || generateRequestId()
  let response: ApiDownloadResponse
  try {
    response = await ipcCall<ApiDownloadResponse>(
      IpcCommand.ApiDownload,
      {
        path,
        params: null,
        headers: { 'X-Request-ID': requestId },
        filename: null,
        inlineOnly: true,
      },
      { timeout: options?.timeout ?? DEFAULT_TIMEOUT, signal: options?.signal ?? undefined },
    )
  } catch (error) {
    if (!options?._isRetry && isAuthIPCError(error)) {
      await refreshToken()
      return fetchApiBlobViaIPC(url, { ...options, _isRetry: true })
    }
    throw error
  }
  if (response.businessCode && response.businessCode !== 0) {
    const message = response.businessMessage || `二进制响应读取失败: ${response.businessCode}`
    handleBusinessFailure(response.businessCode, message)
    throw new BusinessError(response.businessCode, message)
  }
  return base64ToBlob(response.contentBase64, response.contentType)
}

async function fetchApiBlobViaFetch(url: string, options?: RequestOptions): Promise<Blob> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), options?.timeout ?? DEFAULT_TIMEOUT)
  const externalSignal = options?.signal as AbortSignal | undefined
  const abortFromExternal = () => controller.abort(externalSignal?.reason)
  if (externalSignal) {
    if (externalSignal.aborted) abortFromExternal()
    else externalSignal.addEventListener('abort', abortFromExternal, { once: true })
  }

  try {
    const { targetUrl } = normalizeApiBlobTarget(url)
    const headers: Record<string, string> = {
      ...(options?.headers as Record<string, string>),
    }
    if (!headers['X-Request-ID']) headers['X-Request-ID'] = generateRequestId()
    const token = getToken()
    if (token) headers['x-token'] = token
    const response = await fetchWithRetry(targetUrl.toString(), {
      method: 'GET',
      headers,
      cache: options?.cache,
      signal: controller.signal,
    })
    if (response.status === 401) {
      if (options?._isRetry) {
        handleTokenExpired()
        throw new TokenExpiredError()
      }
      await refreshToken()
      return fetchApiBlobViaFetch(url, { ...options, _isRetry: true })
    }
    if (!response.ok) throw new NetworkError(`二进制响应读取失败: ${response.status}`)
    return response.blob()
  } catch (error) {
    if (error instanceof TokenExpiredError) throw error
    if (error instanceof BusinessError) throw error
    if (error instanceof NetworkError) throw error
    if ((error as Error).name === 'AbortError') throw error
    throw new NetworkError((error as Error).message || '二进制响应读取失败')
  } finally {
    externalSignal?.removeEventListener('abort', abortFromExternal)
    clearTimeout(timeout)
  }
}

/** 读取 Boss 同源二进制响应，供短期授权预览等不落盘场景使用。 */
export async function fetchApiBlob(url: string, options?: RequestOptions): Promise<Blob> {
  if (isTauri) return fetchApiBlobViaIPC(url, options)
  return fetchApiBlobViaFetch(url, options)
}

function base64ToBlob(contentBase64: string, contentType: string) {
  const binary = atob(contentBase64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: contentType || 'application/octet-stream' })
}

function isAuthIPCError(error: unknown) {
  return (
    (error as Record<string, unknown>)?.ipcErrorType === 'Auth' ||
    ((error as Error)?.message || '').includes('登录已过期')
  )
}

async function downloadViaIPC(url: string, options?: DownloadOptions): Promise<DownloadResult> {
  const existingHeaders = options?.headers as Record<string, string> | undefined
  const requestId = existingHeaders?.['X-Request-ID'] || generateRequestId()
  let response: ApiDownloadResponse
  try {
    response = await ipcCall<ApiDownloadResponse>(
      IpcCommand.ApiDownload,
      {
        path: url,
        params: paramsToRecord(options?.params),
        headers: { 'X-Request-ID': requestId },
        filename: options?.filename,
      },
      { timeout: options?.timeout ?? DEFAULT_TIMEOUT, signal: options?.signal ?? undefined },
    )
  } catch (error) {
    if (!options?._isRetry && isAuthIPCError(error)) {
      await refreshToken()
      return downloadViaIPC(url, { ...options, _isRetry: true })
    }
    throw error
  }
  if (response.businessCode && response.businessCode !== 0) {
    const message = response.businessMessage || `导出失败: ${response.businessCode}`
    handleBusinessFailure(response.businessCode, message)
    throw new BusinessError(response.businessCode, message)
  }
  const filename = response.filename || options?.filename || 'download'
  if (!response.filePath) {
    const blob = base64ToBlob(response.contentBase64, response.contentType)
    saveBlob(blob, filename)
  }
  return {
    filename,
    locationText: getDefaultDownloadLocationText(),
    filePath: response.filePath ?? undefined,
    downloadDir: response.downloadDir ?? undefined,
  }
}

async function downloadViaFetch(url: string, options?: DownloadOptions): Promise<DownloadResult> {
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  }
  if (!headers['X-Request-ID']) {
    headers['X-Request-ID'] = generateRequestId()
  }
  const token = getToken()
  if (token) {
    headers['x-token'] = token
  }

  let fullUrl = BASE_URL + url
  const params = paramsToRecord(options?.params)
  if (params) {
    const qs = new URLSearchParams(params).toString()
    if (qs) fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs
  }

  const res = await fetch(fullUrl, {
    method: 'GET',
    headers,
    signal: options?.signal,
  })
  if (res.status === 401 && !options?._isRetry) {
    await refreshToken()
    return downloadViaFetch(url, { ...options, _isRetry: true })
  }
  if (!res.ok) {
    try {
      const json = (await res.clone().json()) as Partial<ApiResponse<unknown>>
      if (json.code === PSI_ACCESS_DISABLED_CODE) {
        const message = typeof json.msg === 'string' ? json.msg : `导出失败: ${res.status}`
        handleBusinessFailure(json.code, message)
        throw new BusinessError(json.code, message)
      }
    } catch (error) {
      if (error instanceof BusinessError) throw error
    }
    throw new NetworkError(`导出失败: ${res.status}`)
  }
  // 导出被权限、数量或超时校验拒绝时返回 JSON，不能将错误响应保存为 Excel。
  if (res.headers.get('content-type')?.toLowerCase().includes('application/json')) {
    const json = await parseApiResponse<unknown>(res)
    if (typeof json.code === 'number' && json.code !== 0) {
      const message = json.msg || '导出失败，请稍后重试'
      handleBusinessFailure(json.code, message)
      throw new BusinessError(json.code, message)
    }
    throw new NetworkError('导出响应不是文件，请稍后重试')
  }
  const blob = await res.blob()
  const filename = options?.filename || 'download'
  saveBlob(blob, filename)
  return { filename, locationText: getDefaultDownloadLocationText() }
}

export async function downloadApiFile(
  url: string,
  options?: DownloadOptions,
): Promise<DownloadResult> {
  if (isTauri) {
    return downloadViaIPC(url, options)
  }
  return downloadViaFetch(url, options)
}

export async function downloadBlobFile(blob: Blob, filename: string): Promise<DownloadResult> {
  const safeFilename = filename || 'download'
  if (isTauri) {
    const response = await ipcCall<ApiDownloadResponse>(IpcCommand.SaveDownloadBase64, {
      filename: safeFilename,
      contentBase64: await blobToBase64(blob),
      contentType: blob.type || 'application/octet-stream',
    })
    if (!response.filePath) throw new NetworkError('文件未保存，请重试下载')
    return {
      filename: response.filename || safeFilename,
      locationText: getDefaultDownloadLocationText(),
      filePath: response.filePath ?? undefined,
      downloadDir: response.downloadDir ?? undefined,
    }
  }
  saveBlob(blob, safeFilename)
  return { filename: safeFilename, locationText: getDefaultDownloadLocationText() }
}

async function uploadViaIPC<T>(url: string, file: File, options?: UploadOptions): Promise<T> {
  const existingHeaders = options?.headers as Record<string, string> | undefined
  const requestId = existingHeaders?.['X-Request-ID'] || generateRequestId()
  options?.signal?.throwIfAborted()
  const fileBase64 = await fileToBase64(file)
  const additionalFiles = []
  for (const extra of options?.additionalFiles || []) {
    options?.signal?.throwIfAborted()
    additionalFiles.push({ fileName: extra.name, contentType: extra.type || 'application/octet-stream', fileBase64: await fileToBase64(extra) })
  }
  options?.signal?.throwIfAborted()
  const json = await ipcCall<ApiResponse<T>>(IpcCommand.ApiUpload, {
    path: url,
    params: paramsToRecord(options?.params),
    headers: { 'X-Request-ID': requestId },
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    fileBase64,
    additionalFiles,
    formFields: options?.formFields,
  }, { timeout: options?.timeout, signal: options?.signal ?? undefined })

  if ((json as unknown as Record<string, unknown>)._httpUnauthorized) {
    if (options?._isRetry) {
      handleTokenExpired()
      throw new TokenExpiredError()
    }
    await refreshToken()
    return uploadViaIPC<T>(url, file, { ...options, _isRetry: true })
  }
  if (json.code !== 0) {
    handleBusinessFailure(json.code, json.msg)
    throw new BusinessError(json.code, json.msg)
  }
  return json.data
}

async function uploadPathViaIPC<T>(
  url: string,
  filePath: string,
  options?: UploadOptions,
): Promise<T> {
  const existingHeaders = options?.headers as Record<string, string> | undefined
  const requestId = existingHeaders?.['X-Request-ID'] || generateRequestId()
  const json = await ipcCall<ApiResponse<T>>(IpcCommand.ApiUploadByPath, {
    path: url,
    params: paramsToRecord(options?.params),
    headers: { 'X-Request-ID': requestId },
    filePath,
  })

  if ((json as unknown as Record<string, unknown>)._httpUnauthorized) {
    if (options?._isRetry) {
      handleTokenExpired()
      throw new TokenExpiredError()
    }
    await refreshToken()
    return uploadPathViaIPC<T>(url, filePath, { ...options, _isRetry: true })
  }
  if (json.code !== 0) {
    handleBusinessFailure(json.code, json.msg)
    throw new BusinessError(json.code, json.msg)
  }
  return json.data
}

async function uploadViaFetch<T>(url: string, file: File, options?: UploadOptions): Promise<T> {
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  }
  if (!headers['X-Request-ID']) {
    headers['X-Request-ID'] = generateRequestId()
  }
  const token = getToken()
  if (token) {
    headers['x-token'] = token
  }

  let fullUrl = BASE_URL + url
  const params = paramsToRecord(options?.params)
  if (params) {
    const qs = new URLSearchParams(params).toString()
    if (qs) fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs
  }

  const formData = new FormData()
  formData.append('file', file)
  for (const extra of options?.additionalFiles || []) formData.append('file', extra)
  for (const [key, value] of Object.entries(options?.formFields || {})) formData.append(key, value)
  if (params) {
    Object.entries(params).forEach(([key, value]) => formData.append(key, value))
  }
  const res = await fetch(fullUrl, {
    method: 'POST',
    headers,
    body: formData,
    signal: options?.signal,
  })
  if (res.status === 401 && !options?._isRetry) {
    await refreshToken()
    return uploadViaFetch<T>(url, file, { ...options, _isRetry: true })
  }
  if (res.status === 401) {
    handleTokenExpired()
    throw new TokenExpiredError()
  }
  const json: ApiResponse<T> = await res.json()
  if (json.code !== 0) {
    handleBusinessFailure(json.code, json.msg)
    throw new BusinessError(json.code, json.msg)
  }
  return json.data
}

export async function uploadApiFile<T>(
  url: string,
  file: File,
  params?: Record<string, unknown>,
  options?: UploadOptions,
): Promise<T> {
  const mergedOptions = { ...options, params }
  if (isTauri) {
    return uploadViaIPC<T>(url, file, mergedOptions)
  }
  return uploadViaFetch<T>(url, file, mergedOptions)
}

export async function uploadApiFileByPath<T>(
  url: string,
  filePath: string,
  params?: Record<string, unknown>,
  options?: UploadOptions,
): Promise<T> {
  const mergedOptions = { ...options, params }
  if (!isTauri) {
    throw new NetworkError('当前环境不支持按本地路径上传文件')
  }
  return uploadPathViaIPC<T>(url, filePath, mergedOptions)
}

// ─── HTTP 方法快捷封装 ──────────────────────────────────
export function get<T>(url: string, options?: RequestOptions): Promise<T> {
  return request<T>(url, { ...options, method: 'GET' })
}

export function post<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(url, { ...options, method: 'POST', body: data })
}

export function put<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(url, { ...options, method: 'PUT', body: data })
}

export function patch<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(url, { ...options, method: 'PATCH', body: data })
}

export function del<T>(url: string, options?: RequestOptions): Promise<T> {
  return request<T>(url, { ...options, method: 'DELETE' })
}

// ─── 请求取消工具 ────────────────────────────────────────
export function createAbortGroup() {
  const controller = new AbortController()
  return {
    signal: controller.signal,
    abort: () => controller.abort(),
  }
}

/** 浏览器流式请求入口；使用统一身份、请求标识及登录失效处理，不重放已开始的查询。 */
export async function openAssistantStream(
  body: unknown,
  signal: AbortSignal,
  requestId: string,
): Promise<Response> {
  for (let attempt = 0; attempt < 2; attempt++) {
    signal.throwIfAborted()
    const response = await fetch(`${BASE_URL}/business/assistant/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'x-token': getToken(),
        'X-Request-ID': requestId,
      },
      body: JSON.stringify(body),
      signal,
    })
    if (response.ok && response.headers.get('content-type')?.includes('text/event-stream'))
      return response
    if (response.status === 404)
      throw new BusinessError(404, '当前环境尚未启用智能数据助手，请联系管理员', { status: 404 })
    const json = await parseApiResponse<unknown>(response)
    handleBusinessFailure(json.code, json.msg)
    if (response.status === 401 && json.code !== 9 && json.code !== 9003 && attempt === 0) {
      await refreshToken()
      continue
    }
    if (response.status === 401) {
      handleTokenExpired()
      throw new TokenExpiredError()
    }
    throw new BusinessError(
      json.code,
      businessErrorMessage(json, '助手请求失败'),
      { status: response.status },
    )
  }
  throw new TokenExpiredError()
}
